import { G } from '@mobily/ts-belt';
import { Alert, AlertTitle, List, ListItem, ListItemText } from '@mui/material';
import { useMemo } from 'react';
import { RecordContextProvider, useGetList, useTranslate } from 'react-admin';

import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';

import { IdField } from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

type DuplicateEmployeeNameAlertProps = {
  b2bEmployee: GetB2bEmployeesOneResponse | null;
  testId: string;
};

export const DuplicateEmployeeNameAlert = ({
  b2bEmployee,
  testId,
}: DuplicateEmployeeNameAlertProps) => {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();

  const { data: sameNameEmployees } = useGetList<GetB2bEmployeesOneResponse>(
    `${resourceBasePath}employees`,
    {
      filter: {
        // note: not filtering on company id on purpose in case the incorrect employee id
        // was selected via the employee list (actually happened in prod)
        firstname: b2bEmployee?.firstname,
        lastname: b2bEmployee?.lastname,
      },
      pagination: { page: 1, perPage: 100 },
      sort: { field: 'id', order: 'ASC' },
    },
    {
      enabled: G.isNotNullable(b2bEmployee),
    },
  );

  const duplicateEmployees = useMemo(() => {
    if (G.isNullable(b2bEmployee) || G.isNullable(sameNameEmployees)) {
      return [];
    }

    return sameNameEmployees.filter(
      (employee) =>
        employee.id !== b2bEmployee.id &&
        // firstname and lastname use ilike search, so the name could be a substring instead of equal
        employee.firstname.trim().toLowerCase() === b2bEmployee.firstname.trim().toLowerCase() &&
        employee.lastname.trim().toLowerCase() === b2bEmployee.lastname.trim().toLowerCase(),
    );
  }, [b2bEmployee, sameNameEmployees]);

  if (duplicateEmployees.length === 0) {
    return null;
  }

  return (
    <Alert data-testid={`${testId}_DuplicateEmployeeNameAlert`} severity="warning">
      <AlertTitle>
        {translate('resources.bo_common.b2b.withdrawals.create.errors.duplicate_employee_name')}
      </AlertTitle>
      <List dense disablePadding>
        {duplicateEmployees.map((employee) => (
          <ListItem key={employee.id} disableGutters>
            <RecordContextProvider value={employee}>
              <ListItemText
                primary={
                  <IdField
                    reference={`${resourceBasePath}employees`}
                    data-testid={`${testId}_DuplicateEmployeeNameAlert_EmployeeLink`}
                    customText={translate(
                      'resources.bo_common.b2b.withdrawals.create.errors.duplicate_employee_row',
                      {
                        id: employee.id,
                        company: employee.company_name,
                        nir: employee.nir || '-',
                      },
                    )}
                  />
                }
              />
            </RecordContextProvider>
          </ListItem>
        ))}
      </List>
    </Alert>
  );
};
